/**
 * User Entity - Model Types
 *
 * Domain model types for the User entity.
 * User represents a registered user in the system with their
 * core identity data and system role.
 *
 * @layer entities
 * @slice user
 * @segment model
 * @added DOC-0092 (2025-12-26)
 */

/**
 * System roles matching backend enum
 *
 * @see apps/api/src/db/schema/users.ts - systemRoleEnum
 */
export type SystemRole = 'SYSTEM_ADMIN' | 'DEVELOPER' | 'SYSTEM_CLIENT';

export type BusinessRole =
  | 'Freediver'
  | 'Board member'
  | 'Certification admin'
  | 'School member'
  | 'Helpdesk'
  | 'User viewer'
  | 'Growth'
  | 'Analytics'
  | 'Finance'
  | 'Instructor'
  | 'Reports'
  | 'Developer'
  | 'Super admin'
  | 'Admin'
  | 'BT admin'
  | 'Non-member';

/**
 * Core User domain model
 *
 * Represents a registered user with their identity and role.
 * This is the fundamental user data available to the frontend.
 *
 * @see apps/api/src/routes/v1/auth/oauth.ts - /api/v1/auth/me response
 */
export interface User {
  /** Unique user identifier (UUID) */
  id: string;
  /** User's email address */
  email: string;
  /** User's system-wide role */
  systemRole: SystemRole;
  /** Business/domain roles from the legacy business-role field */
  businessRoles?: BusinessRole[];
  /** User's display name (optional) */
  name?: string;
  /** URL to user's avatar image (optional) */
  avatarUrl?: string;
}

/** Approved business-role catalogue entry for canonical freediver views and filters. */
export const BUSINESS_ROLE_FREEDIVER: BusinessRole = 'Freediver';

/** Approved business-role catalogue entry for canonical instructor views and filters. */
export const BUSINESS_ROLE_INSTRUCTOR: BusinessRole = 'Instructor';

export const BUSINESS_ROLES: readonly BusinessRole[] = [
  BUSINESS_ROLE_FREEDIVER,
  'Board member',
  'Certification admin',
  'School member',
  'Helpdesk',
  'User viewer',
  'Growth',
  'Analytics',
  'Finance',
  BUSINESS_ROLE_INSTRUCTOR,
  'Reports',
  'Developer',
  'Super admin',
  'Admin',
  'BT admin',
  'Non-member',
];

/**
 * Type guard to check if a value is a valid SystemRole
 */
export function isSystemRole(value: unknown): value is SystemRole {
  return (
    typeof value === 'string' && ['SYSTEM_ADMIN', 'DEVELOPER', 'SYSTEM_CLIENT'].includes(value)
  );
}

export function isBusinessRole(value: unknown): value is BusinessRole {
  return typeof value === 'string' && BUSINESS_ROLES.includes(value as BusinessRole);
}

/**
 * Type guard to check if a value is a valid User object
 */
export function isUser(value: unknown): value is User {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.email === 'string' &&
    isSystemRole(obj.systemRole) &&
    (obj.businessRoles === undefined ||
      (Array.isArray(obj.businessRoles) && obj.businessRoles.every(isBusinessRole)))
  );
}
